import { format, isAfter, parseISO } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { useEffect, useState } from 'react'

import { joinDateTime } from '../../../../../utils/datetime-utils'
import { normalizeTrackingWindowTime } from './tracking-window.utils'

/**
 * Determine whether the tracking window has ended based on end date, time, and timezone
 * @param endDate - The end date of the tracking window
 * @param endTime - The end time in 24-hour format (HH:mm)
 * @param timezone - The timezone to use for comparison
 * @param now - The current date/time to compare against
 * @returns true if the tracking window has ended, false otherwise
 */
export const getHasTrackingWindowEnded = (
  endDate: Date | undefined,
  endTime: string,
  timezone: string,
  now: Date
): boolean => {
  if (!endDate || !endTime) return false
  const normalizedEndTime = normalizeTrackingWindowTime(endTime)
  const endDateTimeString = joinDateTime(format(endDate, 'yyyy-MM-dd'), normalizedEndTime)
  if (!endDateTimeString) return false

  const endMoment = toZonedTime(parseISO(endDateTimeString), timezone)
  const nowInTimezone = toZonedTime(now, timezone)

  return isAfter(nowInTimezone, endMoment)
}

export const TrackingEndedIndicator = ({
  endDate,
  endTime,
  timezone
}: {
  endDate: Date | undefined
  endTime: string
  timezone: string
}) => {
  const [now, setNow] = useState<Date>(() => new Date())

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000) // update every minute
    return () => clearInterval(timer)
  }, [])

  const hasTrackingWindowEnded = getHasTrackingWindowEnded(endDate, endTime, timezone, now)

  if (!hasTrackingWindowEnded) return null

  return <span className="text-destructive text-sm">Ended</span>
}
